import { Field as BaseField } from "@base-ui/react/field";
import { Fieldset as BaseFieldset } from "@base-ui/react/fieldset";
import { Radio as BaseRadio } from "@base-ui/react/radio";
import { RadioGroup as BaseRadioGroup } from "@base-ui/react/radio-group";
import clsx from "clsx";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon";
import fieldStyles from "../Field/Field.module.css";
import styles from "./RadioGroup.module.css";

export interface RadioOption<V extends string = string> {
  value: V;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<V extends string = string> {
  /** The group's legend. */
  label: ReactNode;
  description?: ReactNode;
  error?: string | null;
  options: ReadonlyArray<RadioOption<V>>;
  value?: V;
  defaultValue?: V;
  onValueChange?: (value: V) => void;
  orientation?: "vertical" | "horizontal";
  name?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

/** One choice from a short, visible list (Base UI RadioGroup in a Fieldset). */
export function RadioGroup<V extends string = string>({
  label,
  description,
  error,
  options,
  value,
  defaultValue,
  onValueChange,
  orientation = "vertical",
  name,
  disabled,
  required,
  className,
}: RadioGroupProps<V>) {
  return (
    <BaseField.Root
      name={name}
      disabled={disabled}
      invalid={error ? true : undefined}
      className={clsx(fieldStyles.field, className)}
    >
      <BaseFieldset.Root
        className={styles.fieldset}
        render={
          <BaseRadioGroup<V>
            value={value}
            defaultValue={defaultValue}
            onValueChange={onValueChange ? (next) => onValueChange(next) : undefined}
            disabled={disabled}
            required={required}
          />
        }
      >
        <BaseFieldset.Legend className={fieldStyles.label}>{label}</BaseFieldset.Legend>
        {description ? <p className={fieldStyles.description}>{description}</p> : null}
        <div className={styles.options} data-orientation={orientation}>
          {options.map((option) => (
            <BaseField.Item key={option.value} className={styles.item} disabled={option.disabled}>
              <BaseField.Label className={styles.optionLabel}>
                <BaseRadio.Root value={option.value} className={styles.radio}>
                  <BaseRadio.Indicator className={styles.indicator} />
                </BaseRadio.Root>
                <span className={styles.optionText}>
                  <span>{option.label}</span>
                  {option.description ? <span className={styles.optionDescription}>{option.description}</span> : null}
                </span>
              </BaseField.Label>
            </BaseField.Item>
          ))}
        </div>
      </BaseFieldset.Root>
      {error ? (
        <BaseField.Error className={fieldStyles.error} match>
          <Icon name="alertCircle" />
          <span>{error}</span>
        </BaseField.Error>
      ) : null}
    </BaseField.Root>
  );
}
